/**
 * EndScene — shown after Q6 wraps up.
 *
 * Mirrors the cardistry / drop-them-all end screen so balance-out
 * shares the same family look: dim cinema backdrop, spotlight sweep,
 * confetti burst on win, "You Won!" / "Game Over" headline, score +
 * hint stats, and a reward-coin reveal on success.
 *
 * Stats come from GameScene via `this.scene.launch('EndScene', {…})`
 * (see GameScene.onAnswerComplete), so this scene stays a pure view.
 */

const WIDTH = 1280;
const HEIGHT = 720;
const BODY_FONT = 'Athletics-Regular, sans-serif';

export default class EndScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EndScene' });
  }

  init(data) {
    const d = data || {};
    this.score = d.score ?? 0;
    this.attempted = d.attempted ?? 0;
    this.totalHints = d.totalHints ?? 0;
    this.totalAttempts = d.totalAttempts ?? 0;
    this.questionCount = d.questionCount ?? 6;
    this.won = d.won ?? true;
  }

  create() {
    this.cameras.main.fadeIn(500, 255, 255, 255);

    this.endOverlay = this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'endScreenBG')
      .setDisplaySize(WIDTH, HEIGHT)
      .setDepth(0);

    this.spotlight = this.add.sprite(WIDTH / 2, HEIGHT / 2, 'spotlight').setDepth(1);
    this.anims.create({
      key: 'spotlightOpen',
      frames: this.anims.generateFrameNames('spotlight', {
        start: 0,
        end: 188,
        zeroPad: 0,
        prefix: 'spotlight0',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: 0,
    });
    this.anims.create({
      key: 'spotlightClose',
      frames: this.anims.generateFrameNames('spotlight', {
        start: 189,
        end: 214,
        zeroPad: 0,
        prefix: 'spotlight0',
        suffix: '.png',
      }),
      frameRate: 20,
      repeat: 0,
    });

    this.endConfetti = this.add.sprite(WIDTH / 2, HEIGHT / 2, 'endConfetti').setDepth(4);
    this.anims.create({
      key: 'endConfetti',
      frames: this.anims.generateFrameNames('endConfetti', {
        start: 0,
        end: 120,
        zeroPad: 0,
        prefix: 'seq_0_',
        suffix: '.png',
      }),
      frameRate: 40,
      repeat: 0,
    });

    this.time.delayedCall(
      2000,
      function () {
        this.spotlight.play('spotlightOpen');
      },
      [],
      this,
    );

    this.gameOver = this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'youWon')
      .setAlpha(0)
      .setDepth(2);

    this.tweens.add({
      targets: this.gameOver,
      ease: 'Expo.easeInOut',
      duration: 1000,
      delay: 300,
      alpha: { getStart: () => 0, getEnd: () => 1 },
    });

    this.tweens.add({
      targets: [this.gameOver],
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      y: { getStart: () => HEIGHT / 2, getEnd: () => HEIGHT / 2 - 50 },
    });

    this.nextButton = this.add
      .sprite(WIDTH / 2, 740, 'endNextButton')
      .setAlpha(0)
      .setDepth(3)
      .setInteractive({ cursor: 'pointer' });

    this.iconDisplay = this.add
      .sprite(WIDTH / 2 - 80, HEIGHT / 2 + 100, 'correctIcon')
      .setAlpha(0)
      .setDepth(3);

    if (!this.won) {
      this.nextButton.setTexture('retryButton');
      this.gameOver.setTexture('gameOver');
      this.iconDisplay.setTexture('incorrectIcon');
      this.retryText = this.add
        .text(
          WIDTH / 2 - 140,
          HEIGHT / 2 + 200,
          `Solve every problem correctly \n to clear the game`,
          {
            fontFamily: BODY_FONT,
            fontSize: '18px',
            color: '#ffffff',
          },
        )
        .setAlign('center')
        .setAlpha(0)
        .setDepth(3);
      this.tweens.add({
        targets: [this.retryText],
        ease: 'Linear',
        duration: 300,
        delay: 3000,
        alpha: { getStart: () => 0, getEnd: () => 1 },
        y: {
          getStart: () => HEIGHT / 2 + 200,
          getEnd: () => HEIGHT / 2 + 150,
        },
      });
    } else {
      this.endConfetti.play('endConfetti');
    }

    this.finalScore = this.add
      .text(
        WIDTH / 2 - 140,
        HEIGHT / 2 + 140,
        `${this.score}/${this.questionCount} Problems`,
        {
          fontFamily: BODY_FONT,
          fontSize: '18px',
          color: '#ffffff',
        },
      )
      .setAlpha(0)
      .setDepth(3);

    this.hintsUsed = this.add
      .text(
        WIDTH / 2 + 30,
        HEIGHT / 2 + 140,
        `${this.totalHints} Hint${this.totalHints === 1 ? '' : 's'} Used`,
        {
          fontFamily: BODY_FONT,
          fontSize: '18px',
          color: '#ffffff',
        },
      )
      .setAlpha(0)
      .setDepth(3);

    this.hintDisplay = this.add
      .sprite(WIDTH / 2 + 80, HEIGHT / 2 + 100, 'hintIcon')
      .setAlpha(0)
      .setScale(0.8)
      .setDepth(3);

    this.tweens.add({
      targets: [this.iconDisplay, this.hintDisplay],
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      y: {
        getStart: () => HEIGHT / 2 + 100,
        getEnd: () => HEIGHT / 2 + 50,
      },
      alpha: { getStart: () => 0, getEnd: () => 1 },
    });

    this.tweens.add({
      targets: [this.finalScore, this.hintsUsed],
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      alpha: { getStart: () => 0, getEnd: () => 1 },
      y: {
        getStart: () => HEIGHT / 2 + 140,
        getEnd: () => HEIGHT / 2 + 100,
      },
    });

    this.tweens.add({
      targets: [this.nextButton],
      ease: 'Linear',
      duration: 300,
      delay: 3000,
      alpha: { getStart: () => 0, getEnd: () => 1 },
      y: { getStart: () => 740, getEnd: () => 650 },
    });

    // Reward reveal background — full-screen sparkle/burst animation
    // played behind the goldenWeight icon. The atlas frames are named
    // `Reward/Reward00` … `Reward/Reward89` (zero-padded, 2 digits).
    //
    // Plays in two passes:
    //   1. `rewardBgShow` — frames 00 → 89 once (the full intro burst)
    //   2. `rewardBgLoop` — frames 59 → 89 looped forever
    //
    // Only the tail frames are designed to loop seamlessly, so we
    // restrict the looped pass to that range. Bump REWARD_BG_LOOP_START
    // if the seamless segment needs to start earlier/later.
    const REWARD_BG_LOOP_START = 59;
    this.rewardBg = this.add
      .sprite(WIDTH / 2, HEIGHT / 2, 'rewardBg', 'Reward/Reward00')
      .setAlpha(0)
      .setDepth(2);
    this.anims.create({
      key: 'rewardBgShow',
      frames: this.anims.generateFrameNames('rewardBg', {
        start: 0,
        end: 89,
        zeroPad: 2,
        prefix: 'Reward/Reward',
      }),
      frameRate: 30,
      repeat: 0,
    });
    this.anims.create({
      key: 'rewardBgLoop',
      frames: this.anims.generateFrameNames('rewardBg', {
        start: REWARD_BG_LOOP_START,
        end: 89,
        zeroPad: 2,
        prefix: 'Reward/Reward',
      }),
      frameRate: 24,
      repeat: -1,
    });
    this.rewardBg.on(
      Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + 'rewardBgShow',
      () => this.rewardBg.play('rewardBgLoop'),
      this,
    );

    // Golden-weight prize icon — animated through code:
    //   1. fades in + scales up with an overshoot bounce
    //   2. settles into a slow idle bob (y float + tiny tilt)
    this.goldenWeight = this.add
      .image(WIDTH / 2, HEIGHT / 2, 'goldenWeight')
      .setAlpha(0)
      .setScale(0)
      .setDepth(3);

    // Post-rewards CTA — "Play again" replay button + an underlined
    // "Rate This Game" link sitting directly beneath it. Both start
    // hidden and fade in together with the rest of the reward overlay.
    this.okButton = this.add
      .sprite(WIDTH / 2, 710, 'playAgainButton')
      .setAlpha(0)
      .setDepth(4)
      .setInteractive({ cursor: 'pointer' });

    this.rateText = this.add
      .text(WIDTH / 2, 710 + 50, 'Rate This Game', {
        fontFamily: 'Athletics-Bold, Athletics-Regular, sans-serif',
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0)
      .setDepth(4)
      .setInteractive({ cursor: 'pointer' });

    // Phaser Text doesn't support text-decoration, so the underline
    // is drawn as a thin rectangle directly below the glyph baseline.
    this.rateUnderline = this.add
      .rectangle(
        this.rateText.x,
        this.rateText.y + this.rateText.height / 2 + 1,
        this.rateText.width,
        2,
        0xffffff,
      )
      .setAlpha(0)
      .setDepth(4);

    this.nextButton.on('pointerover', () => {
      this.nextButton.setTexture(this.won ? 'nextHover' : 'retryHover');
    });
    this.nextButton.on('pointerout', () => {
      this.nextButton.setTexture(this.won ? 'endNextButton' : 'retryButton');
    });

    this.nextButton.on('pointerup', () => {
      if (!this.won) {
        this.handleReplay();
      } else {
        this.tweens.add({
          targets: [
            this.gameOver,
            this.iconDisplay,
            this.hintDisplay,
            this.finalScore,
            this.hintsUsed,
            this.nextButton,
          ],
          alpha: 0,
          duration: 300,
        });

        this.time.delayedCall(
          1000,
          () => {
            // Background burst plays once and holds on its last frame.
            this.rewardBg.setAlpha(1).play('rewardBgShow');

            // Golden-weight icon — code-driven pop-in, then a gentle
            // floating idle. Kicks off ~0.5s into the burst so the
            // sparkle has time to bloom before the prize lands.
            this.time.delayedCall(500, () => this.animateGoldenWeight());

            this.tweens.add({
              targets: this.okButton,
              ease: 'Linear',
              duration: 300,
              delay: 3000,
              alpha: 1,
              y: 620,
            });
            // Underline + label slide up with the button so they
            // stay glued together visually.
            this.tweens.add({
              targets: [this.rateText, this.rateUnderline],
              ease: 'Linear',
              duration: 300,
              delay: 3000,
              alpha: 1,
              y: '-=90',
            });

            this.add.sprite(577, 120, 'rewardEarned').setOrigin(0).setDepth(3);
            // Centre the "Golden Weight" label horizontally beneath
            // the prize icon so it lines up with the rest of the
            // overlay regardless of the PNG's intrinsic width.
            this.add
              .sprite(WIDTH / 2, HEIGHT / 2 + 170, 'rewardName')
              .setOrigin(0.5, 0.5)
              .setDepth(3);
          },
          [],
          this,
        );
      }
    });

    this.okButton.on('pointerover', () =>
      this.okButton.setTexture('playAgainButtonHover'),
    );
    this.okButton.on('pointerout', () => this.okButton.setTexture('playAgainButton'));
    this.okButton.on('pointerup', () => {
      this.okButton.disableInteractive();
      this.rateText.disableInteractive();
      this.handleReplay();
    });

    // Rate-this-game has no destination yet — fire a console hook so
    // wire-up later doesn't change the visual contract.
    this.rateText.on('pointerup', () => {
      console.info('[EndScene] Rate This Game clicked');
    });
  }

  /**
   * Reveal the golden-weight prize: rises from below at scale 0 with
   * an overshoot pop, settles into a resting position above the
   * mid-line, then runs a subtle vertical bob forever.
   */
  animateGoldenWeight() {
    const restY = HEIGHT / 2 - 40; // slightly above centre — feels lifted
    const startY = restY + 70; // enters from 70 px below the rest

    // Snap to the entry pose before tweening in.
    this.goldenWeight.setPosition(WIDTH / 2, startY);

    this.tweens.add({
      targets: this.goldenWeight,
      alpha: 1,
      scale: 1,
      y: restY,
      duration: 700,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.goldenWeight,
          y: restY - 8,
          duration: 1200,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      },
    });
  }

  /**
   * Replay click — fade to white, then stop the running scenes and
   * restart at StartScene so the full opening sequence plays again.
   */
  handleReplay() {
    this.nextButton.disableInteractive();
    this.cameras.main.fadeOut(500, 255, 255, 255);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.stop('EndScene');
      this.scene.start('StartScene');
    });
  }
}
